import { useEffect, useState } from 'react';
import { api } from '../api';
import { ErrorNotice, Notice, Spinner, formatMoney } from './ui';
import type { RenewalPreview } from '../types';

/**
 * Rolling the membership forward into a year.
 *
 * The most consequential button in the system: it creates a bill for every
 * member at once. So it shows exactly who and exactly how much before it will
 * do anything, everyone is ticked individually, and the figures come back
 * from the server rather than being totted up here — what gets charged is
 * what the server worked out, not what a browser displayed.
 */
export function RenewMembersCard({ yearId, year }: { yearId: number; year: number }) {
  const [preview, setPreview] = useState<RenewalPreview | null>(null);
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ created: number; total_due: number } | null>(null);

  function load() {
    setError(null);
    api
      .get<RenewalPreview>(`/membership-years/${yearId}/renewal-preview`)
      .then((result) => {
        setPreview(result);
        // Everyone who can renew starts ticked, except anyone who never paid
        // last year — inviting them again is a judgement, so it is a
        // deliberate tick rather than a default.
        setChosen(new Set(result.renewable.filter((r) => !r.unpaid_last_year).map((r) => r.member_id)));
      })
      .catch(setError);
  }

  useEffect(load, [yearId]);

  function toggle(memberId: number) {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }

  async function renew() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ created: number; total_due: number }>(
        `/membership-years/${yearId}/renewals`,
        { member_ids: [...chosen] },
      );
      setDone(result);
      load();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  const selectedTotal = (preview?.renewable ?? [])
    .filter((r) => chosen.has(r.member_id))
    .reduce((sum, r) => sum + r.total_due, 0);

  return (
    <div className="card">
      <h2>Renew members into {year}</h2>

      <ErrorNotice error={error} />
      {preview === null && error === null ? <Spinner label="Working out who can renew" /> : null}

      {done !== null ? (
        <Notice kind="success">
          {done.created} {done.created === 1 ? 'member' : 'members'} renewed into {year},{' '}
          {formatMoney(done.total_due)} now due. They will be chased by the reminder schedule and
          tick off through the usual payments screen.
        </Notice>
      ) : null}

      {preview !== null && preview.source_year === null ? (
        <p className="muted">
          There is no {year - 1} on file, so there is nobody to roll forward. Members join through
          the application form instead.
        </p>
      ) : null}

      {preview !== null && preview.source_year !== null ? (
        <>
          <p className="muted small">
            Everyone who held a subscription in {preview.source_year.year}, priced at{' '}
            {year}&apos;s rates, carrying their colony count forward. Donations are not carried
            forward — a donation is a one-off gift, not a standing order.
          </p>

          {preview.renewable.length === 0 && preview.blocked.length === 0 ? (
            <p className="muted">
              {preview.already.length > 0
                ? `Everybody eligible has already been renewed into ${year}.`
                : 'Nobody is eligible to renew into this year.'}
            </p>
          ) : null}

          {preview.renewable.length > 0 ? (
            <>
              <table className="members">
                <thead>
                  <tr>
                    <th scope="col">
                      <input
                        type="checkbox"
                        aria-label="Select everybody"
                        checked={chosen.size === preview.renewable.length}
                        onChange={(e) =>
                          setChosen(
                            e.target.checked
                              ? new Set(preview.renewable.map((r) => r.member_id))
                              : new Set(),
                          )
                        }
                      />
                    </th>
                    <th scope="col">Member</th>
                    <th scope="col">Category</th>
                    <th scope="col">Colonies</th>
                    <th scope="col">Will be charged</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.renewable.map((row) => (
                    <tr key={row.member_id}>
                      <td data-label="">
                        <input
                          type="checkbox"
                          aria-label={`Renew ${row.name}`}
                          checked={chosen.has(row.member_id)}
                          onChange={() => toggle(row.member_id)}
                        />
                      </td>
                      <td data-label="Member">
                        {row.name}
                        {row.unpaid_last_year ? (
                          <span className="muted block small">
                            did not pay for {preview.source_year?.year}
                          </span>
                        ) : null}
                        {row.email === null ? (
                          <span className="muted block small">no email — will need a letter</span>
                        ) : null}
                      </td>
                      <td data-label="Category">{row.category_name}</td>
                      <td data-label="Colonies">{row.colony_count ?? '—'}</td>
                      <td data-label="Will be charged">
                        {formatMoney(row.total_due)}
                        {row.needs_bdi_referral ? (
                          <span className="muted block small">BDI referral needed</span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="actions">
                <button type="button" className="primary" disabled={busy || chosen.size === 0} onClick={() => void renew()}>
                  {busy
                    ? 'Renewing…'
                    : `Renew ${chosen.size} ${chosen.size === 1 ? 'member' : 'members'} — ${formatMoney(selectedTotal)}`}
                </button>
              </div>
            </>
          ) : null}

          {/* Never silently dropped: a member nobody can price is a member
              who would otherwise just vanish from the year. */}
          {preview.blocked.length > 0 ? (
            <>
              <Notice kind="warning">
                {preview.blocked.length} {preview.blocked.length === 1 ? 'member' : 'members'} cannot
                be renewed yet. They are not charged anything and nothing has been created for them.
              </Notice>
              <ul className="permission-list">
                {preview.blocked.map((row) => (
                  <li key={row.member_id}>
                    <strong>{row.name}</strong> — {row.blocked_reason}
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {preview.already.length > 0 ? (
            <p className="muted small">
              {preview.already.length} already {preview.already.length === 1 ? 'has' : 'have'} a{' '}
              {year} subscription and {preview.already.length === 1 ? 'is' : 'are'} not listed again.
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
